import React, { useState, useRef } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';

const DriverDetails = ({ driverData }) => {
  const [activeTab, setActiveTab] = useState('compliance');

  const personalRef = useRef(null);
  const licenseRef = useRef(null);
  const vehicleRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTabClick = (tab, ref) => {
    setActiveTab(tab);
    scrollToSection(ref);
  };

  const driverInfo = {
    fullName: driverData?.fullName || driverData?.name,
    dateOfBirth: driverData?.dateOfBirth
      ? moment(driverData?.dateOfBirth).format('DD MMM YYYY')
      : '',
    licenseNumber: driverData?.drivingLicense,
    fatherName: driverData?.fatherName,
    currentStatus: driverData?.expiryDate
      ? moment(driverData?.expiryDate).isAfter(moment())
        ? 'Valid'
        : 'Expired'
      : '',
    typeOfLicense: driverData?.typeOfLicense || driverData?.licenseType,
    issuingRTO: driverData?.rtoName,
    stateOfRegistration: driverData?.rtoState,
    issueDate: driverData?.issueDate
      ? moment(driverData?.issueDate).format('DD MMM YYYY')
      : '',
    expiryDate: driverData?.expiryDate
      ? moment(driverData?.expiryDate).format('DD MMM YYYY')
      : '',
    categoriesOfVehicles:
      driverData?.categoriesOfVehicles || driverData?.vehicleClass,
    endorsements: driverData?.endorsements,
  };

  const getDateStatus = (dateStr) => {
    if (!dateStr) return 'standard';
    const date = moment(dateStr, 'DD MMM YYYY');
    const today = moment().startOf('day');
    if (!date.isValid()) return 'standard';
    return date.isBefore(today) ? 'expired' : 'valid';
  };

  const shouldShowDocumentIcon = (fieldName) =>
    ['expiryDate'].includes(fieldName);

  const vehicleClasses = driverData?.licenses
    ?.map((l) => l.vehicleType)
    .filter(Boolean)
    .join(', ');
  const endorsements = driverData?.licenses
    ?.map((l) => l.endorsementNumber)
    .filter(Boolean)
    .join(', ');

    const typeOfLicense = [
      ...new Set(
        driverData?.licenses
          ?.map((license) => license.licenseType)
          .filter(Boolean),
      ),
    ].join(', ');

  return (
    <div className={styles.vehicleDetailsContainer}>
      {/* Tab Navigation */}
      <div className={styles.vehicleDetailsTabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'compliance' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('compliance', personalRef)}
        >
          Compliance Details
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'dates' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('dates', licenseRef)}
        >
          Important Dates
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'other' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('other', vehicleRef)}
        >
          Other Details
        </button>
      </div>

      {/* Compliance Details Section */}
      <div
        ref={personalRef}
        className={`${styles.detailsSection} ${styles.tableBtmMrg}`}
      >
        <h2 className={styles.sectionTitle}>Compliance Details</h2>
        <div className={styles.detailsTable}>
          {[
            ['Full Name', driverInfo.fullName],
            ['Date Of Birth', driverInfo.dateOfBirth],
            ['License Number', driverInfo.licenseNumber],
            ["Father's Name", driverInfo.fatherName],
            ['Current Status', driverInfo.currentStatus],
            ['Type Of Licence', typeOfLicense],
            ['Issuing RTO', driverInfo.issuingRTO],
            ['State Of Registration', driverInfo.stateOfRegistration],
          ].map(([label, value]) => (
            <div className={styles.tableRow} key={label}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Important Dates Section */}
      <div
        ref={licenseRef}
        className={`${styles.detailsSection} ${styles.tableBtmMrg}`}
      >
        <h2 className={styles.sectionTitle}>Important Dates</h2>
        <div className={styles.detailsTable}>
          {[
            ['Issue Date', driverInfo.issueDate],
            ['Expiry Date', driverInfo.expiryDate],
          ].map(([label, value]) => (
            <div className={styles.tableRow} key={label}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                {label === 'Expiry Date' ? (
                  <>
                    <span
                      className={`${styles.dateValue} ${
                        styles['date-' + getDateStatus(value)]
                      }`}
                    >
                      {value}
                    </span>
                    {shouldShowDocumentIcon('expiryDate') && (
                      <i
                        className={`${styles.documentIcon} far fa-file-pdf`}
                      ></i>
                    )}
                  </>
                ) : (
                  value
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Other Details Section */}
      <div ref={vehicleRef} className={styles.detailsSection}>
        <h2 className={styles.sectionTitle}>Other Details</h2>
        <div className={styles.detailsTable}>
          {[
            ['Categories Of Vehicles', vehicleClasses],
            ['Endorsements', endorsements],
          ].map(([label, value]) => (
            <div className={styles.tableRow} key={label}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverDetails;
